import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Search, ThumbsUp, ThumbsDown, ChevronLeft } from 'lucide-react'
import { carePlans } from '../data/carePlans'
import { specialties } from '../data/specialties'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { useScrollBehavior } from '../hooks/useScrollBehavior'
import { useFeedback } from '../hooks/useFeedback'
import Footer from '../components/Footer'
import CarePlanHeader from '../components/care-plan/CarePlanHeader'
import CarePlanActions from '../components/care-plan/CarePlanActions'
import PGIEContainer from '../components/pgie/PGIEContainer'
import FloatingSearchBox from '../components/FloatingSearchBox'

const CarePlanPage = () => {
  const { carePlanId } = useParams()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [feedbackGiven, setFeedbackGiven] = useState(false)
  const [feedbackType, setFeedbackType] = useState('')
  const [feedbackName, setFeedbackName] = useState('')
  const [feedbackEmail, setFeedbackEmail] = useState('')
  const [feedbackComments, setFeedbackComments] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Use the feedback hook for Supabase integration
  const { submitFeedback: submitToSupabase, isSubmitting, submitSuccess, submitError, resetState } = useFeedback()

  // Scroll to top on page load
  useScrollToTop()

  // Scroll behavior for mobile
  const { isHeroVisible, showFloatingSearch } = useScrollBehavior()

  const carePlan = carePlans.find(cp => cp.id === carePlanId)
  const specialty = carePlan ? specialties.find(s => s.id === carePlan.specialtyId) : null





  const handleSearch = (e) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      setShowSuggestions(false)
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`)
    }
  }

  const handleSearchInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)

    if (value.trim().length > 0) {
      // Generate suggestions from care plans and specialties
      const lowercaseQuery = value.toLowerCase()
      const suggestions = []

      // Get care plan suggestions
      carePlans.forEach(plan => {
        if (plan.title.toLowerCase().includes(lowercaseQuery) ||
            plan.description.toLowerCase().includes(lowercaseQuery) ||
            plan.category.toLowerCase().includes(lowercaseQuery)) {
          suggestions.push({
            id: plan.id,
            title: plan.title,
            type: 'care-plan',
            category: plan.category
          })
        }
      })

      // Get specialty suggestions
      specialties.forEach(specialty => {
        if (specialty.name.toLowerCase().includes(lowercaseQuery) ||
            specialty.description.toLowerCase().includes(lowercaseQuery)) {
          suggestions.push({
            id: specialty.id,
            title: specialty.name,
            type: 'specialty',
            category: 'Specialty'
          })
        }
      })

      // Limit to 5 suggestions and sort
      const limitedSuggestions = suggestions
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, 5)

      setSearchSuggestions(limitedSuggestions)
      setShowSuggestions(limitedSuggestions.length > 0)
    } else {
      setSearchSuggestions([])
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false)
    setSearchTerm('')

    if (suggestion.type === 'care-plan') {
      navigate(`/care-plan/${suggestion.id}`)
    } else {
      navigate(`/specialty/${suggestion.id}`)
    }
  }

  const handleFeedback = (type) => {
    setFeedbackType(type)
  }

  const submitFeedback = async () => {
    if (!feedbackType) return

    const feedbackData = {
      type: feedbackType,
      name: feedbackName.trim() || null,
      email: feedbackEmail.trim() || null,
      comments: feedbackComments.trim() || null,
      carePlanId,
      carePlanTitle: carePlan.title
    }

    // Submit to Supabase
    const result = await submitToSupabase(feedbackData)

    if (result.success) {
      setFeedbackGiven(true)
      // Reset form
      setFeedbackName('')
      setFeedbackEmail('')
      setFeedbackComments('')
      setFeedbackType('')
    }
  }

  if (!carePlan) {
    return (
      <div className="App">
        <div className="container">
          <h1>Care Plan not found</h1>
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    )
  }

  return (
    <div className="App">
      {/* Fixed Top Search Box - hide on mobile when hero is hidden */}
      <section className={`care-plan-search-fixed ${!isHeroVisible ? 'hidden-mobile' : ''}`}>
        <div className="container">
          <div className="search-header-wrapper">
            <button
              onClick={() => navigate(-1)}
              className="search-header-back-btn"
            >
              <ChevronLeft size={14} />
            </button>
            <div
              className="search-header-title clickable"
              onClick={() => navigate('/')}
            >
              Qmed Nursing Care Plans
            </div>
          </div>
          <form onSubmit={handleSearch} className="search-form-compact">
            <div className="search-input-container">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search other care plans..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                onFocus={() => searchTerm.trim() && setShowSuggestions(searchSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="search-input-compact"
              />

              {/* Search Suggestions Dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="search-suggestions">
                  {searchSuggestions.map((suggestion) => (
                    <div
                      key={`${suggestion.type}-${suggestion.id}`}
                      className="search-suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="suggestion-title">{suggestion.title}</div>
                      <div className="suggestion-category">{suggestion.category}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      <CarePlanHeader carePlan={carePlan} specialty={specialty} isHeroVisible={isHeroVisible} />

      <div className="container">
        <div className="care-plan-content-compact">
          <PGIEContainer pgie={carePlan.pgie} />

          {/* Action Sections Row - Side by Side */}
          <div className="action-sections-row">
            <CarePlanActions carePlan={carePlan} carePlanId={carePlanId} />

            {/* Review & Feedback Section */}
            <div className="feedback-section-comprehensive">
              <h3>Feedback & Review</h3>
              <p className="feedback-subtitle">Help us improve this tool by sharing your feedback</p>

              {!feedbackGiven ? (
                <div className="feedback-form">
                  {/* Was this tool helpful? */}
                  <div className="feedback-question">
                    <label>Was this tool helpful?</label>
                    <div className="feedback-buttons-row">
                      <button
                        onClick={() => handleFeedback('helpful')}
                        className={`feedback-btn-comprehensive helpful ${feedbackType === 'helpful' ? 'selected' : ''}`}
                      >
                        <ThumbsUp size={18} />
                        Helpful
                      </button>
                      <button
                        onClick={() => handleFeedback('not-helpful')}
                        className={`feedback-btn-comprehensive not-helpful ${feedbackType === 'not-helpful' ? 'selected' : ''}`}
                      >
                        <ThumbsDown size={18} />
                        Not Helpful
                      </button>
                    </div>
                  </div>

                  {/* Name field */}
                  <div className="feedback-field">
                    <label>Name (Optional)</label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={feedbackName}
                      onChange={(e) => setFeedbackName(e.target.value)}
                      className="feedback-input"
                    />
                  </div>

                  {/* Email field */}
                  <div className="feedback-field">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      value={feedbackEmail}
                      onChange={(e) => setFeedbackEmail(e.target.value)}
                      className="feedback-input"
                    />
                  </div>

                  {/* Comments field */}
                  <div className="feedback-field">
                    <label>Comments</label>
                    <textarea
                      placeholder="Share your feedback or suggestions..."
                      value={feedbackComments}
                      onChange={(e) => setFeedbackComments(e.target.value)}
                      className="feedback-textarea"
                      rows="4"
                    />
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={submitFeedback}
                    className="feedback-submit-btn"
                    disabled={!feedbackType || isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>

                  {/* Error message */}
                  {submitError && (
                    <div style={{
                      color: '#dc2626',
                      fontSize: '0.9rem',
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '0.375rem'
                    }}>
                      Error: {submitError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="feedback-thanks-comprehensive">
                  <p>Thank you for your feedback!<br/>Your input helps us enhance our care plans.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Search Box for Mobile */}
      <FloatingSearchBox isVisible={showFloatingSearch} />

      <Footer />
    </div>
  )
}

export default CarePlanPage
